import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../../lib/apiClient';
import { cn } from '@vd/ui';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import type { PeriodValue } from './PeriodPicker';
import { planClass } from './planBadge';
import styles from './Admin.module.css';

/**
 * Admin → Usage → Costs (TOADO-241).
 *
 * Per-account cost, margin, and health over the selected window. Numbers
 * come from GET /admin/costs: units are metered nightly (usage_daily) and
 * dollars are derived at read time from the dated rate table — nothing
 * rendered here is ever stored as a dollar figure.
 *
 * Egress is MODELLED (COST_MODEL §2.2, ~70 MB/ticket), storage and AI are
 * measured; the breakdown tooltip and the header hint both say so, because
 * a confidently wrong egress number is exactly the thing that would steer
 * a pricing decision.
 */

interface CostBreakdown {
  storageCents: number;
  aiCents: number;
  transcriptionCents: number;
  egressModelledCents: number;
  totalCents: number;
  storageMeasuredDays: number;
}

interface CostCompany {
  id: string;
  name: string;
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  memberCount: number;
  createdAt: string;
  lastActivityAt: string | null;
  costMonthlyRunRateCents: number;
  dormant: boolean;
  neverActivated: boolean;
  ticketsCreated: number;
  capturesUploaded: number;
  sourceMix: Record<string, number>;
  storageBytesAdded: number;
  storageBytesHeld: number;
  aiTokensInput: number;
  aiTokensOutput: number;
  transcriptionSeconds: number;
  apiRequests: number;
  peakDailyActiveUsers: number;
  cost: CostBreakdown;
  mrrCents: number | null;
  marginCents: number | null;
}

interface CostsResponse {
  period: string;
  from: string;
  to: string;
  windowDays: number;
  egressIsModelled: true;
  companies: CostCompany[];
  totals: {
    costCents: number;
    costMonthlyRunRateCents: number;
    mrrCents: number;
    storageBytesHeld: number;
    aiTokensInput: number;
    aiTokensOutput: number;
    transcriptionSeconds: number;
  };
}

export function fmtCents(cents: number | null): string {
  if (cents === null) return '-';
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${Math.abs(cents / 100).toFixed(2)}`;
}

export function fmtBytes(bytes: number): string {
  if (bytes === 0) return '-';
  if (bytes < 1e6) return `${(bytes / 1e3).toFixed(0)} KB`;
  if (bytes < 1e9) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e9).toFixed(2)} GB`;
}

export function fmtTokens(n: number): string {
  if (n === 0) return '0';
  if (n < 1e3) return String(n);
  if (n < 1e6) return `${(n / 1e3).toFixed(1)}k`;
  return `${(n / 1e6).toFixed(2)}M`;
}

function fmtRelativeDay(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

/** Short source-mix summary, biggest first: "ext 12 · wid 3 · man 1". */
const SOURCE_ABBREV: Record<string, string> = {
  extension: 'ext', widget: 'wid', public_link: 'link', team_snippet: 'snip', mcp: 'mcp', manual: 'man',
};
function fmtSourceMix(mix: Record<string, number>): string {
  const entries = Object.entries(mix).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return '-';
  return entries.map(([k, n]) => `${SOURCE_ABBREV[k] ?? k} ${n}`).join(' · ');
}

export function CostsView({ period }: { period: PeriodValue }) {
  const q = `from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`;
  const [flagFilter, setFlagFilter] = useState<'all' | 'dormant' | 'neverActivated' | 'lossMaking'>('all');
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'costs', q],
    queryFn: () => get<CostsResponse>(`/admin/costs?${q}`),
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const rows = data?.companies ?? [];
    switch (flagFilter) {
      case 'dormant': return rows.filter((c) => c.dormant);
      case 'neverActivated': return rows.filter((c) => c.neverActivated);
      case 'lossMaking': return rows.filter((c) => c.marginCents !== null && c.marginCents < 0);
      default: return rows;
    }
  }, [data, flagFilter]);

  const columns: AdminTableColumn<CostCompany>[] = [
    {
      key: 'name', label: 'Account', sortable: true, sortAccessor: (r) => r.name.toLowerCase(),
      render: (c) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>{c.name}</span>
          <span className={styles.userEmail}>
            {c.memberCount} member{c.memberCount === 1 ? '' : 's'} · {fmtSourceMix(c.sourceMix)}
          </span>
        </div>
      ),
    },
    {
      key: 'plan', label: 'Plan', sortable: true, sortAccessor: (r) => r.plan,
      render: (c) => <span className={cn(styles.planBadge, planClass(c.plan))}>{c.plan}</span>,
    },
    {
      key: 'held', label: 'Storage held', numeric: true, sortable: true,
      sortAccessor: (r) => r.storageBytesHeld,
      title: 'Latest nightly snapshot of capture bytes held (soft-deleted included until reaped)',
      render: (c) => <span>{fmtBytes(c.storageBytesHeld)}</span>,
    },
    {
      key: 'ai', label: 'AI tokens', numeric: true, sortable: true,
      sortAccessor: (r) => r.aiTokensInput + r.aiTokensOutput,
      title: 'Input / output tokens in the window (measured from audit metadata)',
      render: (c) => (
        c.aiTokensInput + c.aiTokensOutput === 0
          ? <span className={styles.muted}>-</span>
          : <span>{fmtTokens(c.aiTokensInput)} / {fmtTokens(c.aiTokensOutput)}</span>
      ),
    },
    {
      key: 'cost', label: 'Est. cost', numeric: true, sortable: true,
      sortAccessor: (r) => r.cost.totalCents,
      title: 'Storage and AI measured; transcription uses provider-reported duration on success and admitted audio duration on failed submissions; egress MODELLED from tickets created',
      render: (c) => {
        // Coverage only matters when there was storage to have measured
        // in or around this window: currently-held bytes, storage cost
        // observed in the window, or bytes added during it. A company
        // with none of those is measured-at-zero, and flagging it
        // "0/N days" would read as missing telemetry (rounds 2 + 3 —
        // round 2's currently-held-only gate wrongly suppressed the
        // caveat for a company that emptied AFTER a partially-measured
        // window).
        const storageRelevant = c.storageBytesHeld > 0 || c.cost.storageCents > 0 || c.storageBytesAdded > 0;
        const coverage = data && storageRelevant && c.cost.storageMeasuredDays < data.windowDays
          ? ` · storage measured ${c.cost.storageMeasuredDays}/${data.windowDays} days`
          : '';
        return (
          <span title={`storage ${fmtCents(c.cost.storageCents)} · ai ${fmtCents(c.cost.aiCents)} · transcription ${fmtCents(c.cost.transcriptionCents)} · egress ~${fmtCents(c.cost.egressModelledCents)} (modelled)${coverage}`}>
            <strong>{fmtCents(c.cost.totalCents)}</strong>
            {coverage !== '' && <span className={styles.muted} title={`Storage snapshots exist for ${c.cost.storageMeasuredDays} of ${data!.windowDays} days in this window; unmeasured days contribute no storage cost.`}>*</span>}
          </span>
        );
      },
    },
    {
      key: 'mrr', label: 'MRR', numeric: true, sortable: true,
      title: 'Plan, add-ons and widget tier, at list price',
      sortAccessor: (r) => r.mrrCents ?? -1,
      render: (c) => c.mrrCents === null ? <span className={styles.muted}>-</span> : fmtCents(c.mrrCents),
    },
    {
      key: 'margin', label: 'Margin', numeric: true, sortable: true,
      sortAccessor: (r) => r.marginCents ?? Number.NEGATIVE_INFINITY,
      title: 'MRR minus the monthly run-rate of estimated cost (window cost × 30 ÷ window days). Blank when the account pays for nothing priced (Free or Enterprise with no add-ons or widget tier).',
      render: (c) => (
        c.marginCents === null
          ? <span className={styles.muted}>-</span>
          : (
            <strong style={{ color: c.marginCents < 0 ? 'var(--danger)' : 'var(--success, #059669)' }}>
              {fmtCents(c.marginCents)}
            </strong>
          )
      ),
    },
    {
      key: 'activity', label: 'Last activity', sortable: true,
      sortAccessor: (r) => r.lastActivityAt ?? '',
      render: (c) => (
        <span className={styles.muted}>
          {fmtRelativeDay(c.lastActivityAt)}
          {c.dormant && <span className={styles.overrideBadge} title="Paying, but no member activity for 30 days">dormant</span>}
          {c.neverActivated && <span className={styles.overrideBadge} title="Signed up, never uploaded a capture">no capture</span>}
        </span>
      ),
    },
  ];

  if (error) return <p style={{ color: 'var(--danger)' }}>Failed to load: {String(error)}</p>;
  if (isLoading || !data) return <p className={styles.muted}>Loading…</p>;

  // Same definition as the per-row margin: monthly MRR minus the MONTHLY
  // run-rate of cost. Review round 2: the card previously subtracted the
  // raw window cost, so a 400-day window showed a deeply negative platform
  // margin above rows showing positive run-rate margins.
  const totalMargin = data.totals.mrrCents - data.totals.costMonthlyRunRateCents;
  return (
    <>
      <div className={styles.cardsRow}>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Window cost ({data.period})</div>
          <div className={styles.cardValue}>{fmtCents(data.totals.costCents)}</div>
          <div className={styles.cardHint}>egress modelled, transcription attempt-aware</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>MRR (plans, add-ons, tiers)</div>
          <div className={styles.cardValue}>{fmtCents(data.totals.mrrCents)}</div>
          <div className={styles.cardHint}>{fmtCents(data.totals.costMonthlyRunRateCents)}/mo cost run-rate</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Margin (monthly)</div>
          <div className={styles.cardValue} style={{ color: totalMargin < 0 ? 'var(--danger)' : undefined }}>
            {fmtCents(totalMargin)}
          </div>
          <div className={styles.cardHint}>{fmtBytes(data.totals.storageBytesHeld)} held platform-wide</div>
        </div>
      </div>

      <div className={styles.tableControls}>
        <select
          className={styles.selectInput}
          style={{ maxWidth: 200 }}
          value={flagFilter}
          onChange={(e) => setFlagFilter(e.target.value as typeof flagFilter)}
          aria-label="Filter accounts by flag"
        >
          <option value="all">All accounts</option>
          <option value="lossMaking">Loss-making</option>
          <option value="dormant">Dormant (paying, quiet 30d)</option>
          <option value="neverActivated">Never captured</option>
        </select>
      </div>

      <AdminTable
        columns={columns}
        rows={filtered}
        getRowId={(c) => c.id}
        searchTextFor={(c) => `${c.name} ${c.id} ${c.plan}`}
        searchPlaceholder="Search accounts…"
        initialSortKey="cost"
      />
    </>
  );
}
